export interface LayerExportProgress {
  readonly layerKey: string;
  readonly percent: number;
  readonly message: string;
  readonly current?: number;
  readonly total?: number;
  readonly hasError?: boolean;
  readonly cancelled?: boolean;
}

export type ExportProgressPhase =
  | 'querying'
  | 'fetching_attributes'
  | 'building_records'
  | 'preparing_features'
  | 'creating_download'
  | 'completed'
  | 'cancelled'
  | 'error';

export function normalizeLegendLayerKey(key: string): string {
  return key.replace(/^favourites_/, '');
}

function parseExportProgressCounts(
  message: string,
): { current?: number; total?: number } {
  const match = message.match(/\((\d+)\/(\d+)\)/);
  if (!match) {
    return {};
  }
  return {
    current: Number(match[1]),
    total: Number(match[2]),
  };
}

interface ExportProgressCounts {
  readonly current?: number;
  readonly total?: number;
}

function shouldResetExportProgressCounts(message: string): boolean {
  return message.startsWith('Querying ');
}

export function resolveExportProgressPhase(
  progress: LayerExportProgress,
): ExportProgressPhase {
  if (progress.cancelled) {
    return 'cancelled';
  }
  if (progress.hasError) {
    return 'error';
  }
  if (
    progress.message === 'Completed' ||
    progress.message === 'Download ready'
  ) {
    return 'completed';
  }
  if (progress.message.includes('Creating download')) {
    return 'creating_download';
  }
  if (progress.message.includes('Preparing features')) {
    return 'preparing_features';
  }
  if (
    progress.message.includes('Building shapefile records') ||
    progress.message.includes('Building shapefile')
  ) {
    return 'building_records';
  }
  if (
    progress.message.includes('Fetching attributes') ||
    progress.message.includes('Loading attributes')
  ) {
    return 'fetching_attributes';
  }
  if (progress.message.startsWith('Querying ')) {
    return 'querying';
  }
  if (progress.percent >= 100) {
    return 'completed';
  }
  return 'fetching_attributes';
}

const EXPORT_PROGRESS_PHASE_LABELS: Record<
  ExportProgressPhase,
  string
> = {
  querying: 'Querying layer',
  fetching_attributes: 'Fetching attributes',
  building_records: 'Building shapefile records',
  preparing_features: 'Preparing features',
  creating_download: 'Creating download',
  completed: 'Completed',
  cancelled: 'Cancelled',
  error: 'Export failed',
};

export function buildExportProgressUpdate(
  layerKey: string,
  message: string,
  prev: LayerExportProgress | null | undefined,
  countsOverride?: ExportProgressCounts,
): LayerExportProgress {
  const counts = parseExportProgressCounts(message);
  const resetCounts = shouldResetExportProgressCounts(message);

  const current = resetCounts
    ? (countsOverride?.current ?? counts.current)
    : (countsOverride?.current ?? counts.current ?? prev?.current);
  const total = resetCounts
    ? (countsOverride?.total ?? counts.total)
    : (countsOverride?.total ?? counts.total ?? prev?.total);

  return {
    layerKey,
    percent: progressFromExportMessage(message, prev?.percent ?? 0),
    message,
    ...(current != null ? { current } : {}),
    ...(total != null ? { total } : {}),
  };
}

export function formatExportProgressPhaseLabel(
  progress: LayerExportProgress,
): string {
  const phase = resolveExportProgressPhase(progress);

  if (phase === 'error') {
    return progress.message;
  }

  const label = EXPORT_PROGRESS_PHASE_LABELS[phase];

  if (phase === 'cancelled' || phase === 'completed') {
    return label;
  }

  return `${label}…`;
}

export function formatExportProgressCountText(
  progress: LayerExportProgress,
): string {
  if (progress.hasError || progress.cancelled) {
    return '';
  }

  const phase = resolveExportProgressPhase(progress);
  if (phase !== 'fetching_attributes' && phase !== 'preparing_features') {
    return '';
  }

  if (
    progress.current != null &&
    progress.total != null &&
    progress.total > 0
  ) {
    return `${progress.current}/${progress.total}`;
  }

  return '';
}
function progressFromExportMessage(
  message: string,
  currentPercent = 0,
): number {
  const countMatch = message.match(/\((\d+)\/(\d+)\)/);
  if (countMatch) {
    const done = Number(countMatch[1]);
    const total = Math.max(Number(countMatch[2]), 1);
    const ratio = done / total;

    if (message.includes('Preparing features')) {
      return 85 + Math.round(ratio * 5);
    }

    return 10 + Math.round(ratio * 75);
  }

  if (message.startsWith('Querying ')) return 5;
  if (
    message.includes('Building shapefile records') ||
    message.includes('Building shapefile')
  ) {
    return 82;
  }
  if (message.includes('Preparing features')) {
    return 85;
  }
  if (message.includes('Creating download')) {
    return Math.max(currentPercent, 92);
  }
  if (message === 'Completed' || message === 'Download ready') {
    return 100;
  }

  return currentPercent > 0 ? currentPercent : 15;
}
